import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import QUERY from 'utils/query';
import STRING from 'utils/string';
import useQuery from 'utils/hooks/useQuery';
import DateFilter from './date';
import ListFilter from './list';
import NumberFilter from './number';
import StringFilter from './string';
import { EFilterConfigType, IFilterConfig } from './types';
import accessLocalStorage from 'services/localStorage';

interface IProps {
  config: IFilterConfig[];
  queryParams?: string; // TODO: Optional till full migration
  changeKey?: string;
  changeKeyUpdateHandlerCb?: (arg: IFilterConfig[]) => IFilterConfig[]; // callback to update filter config based on changeKey updates
}

const Filter = ({
  config,
  queryParams = '',
  changeKey,
  changeKeyUpdateHandlerCb,
}: IProps): JSX.Element => {
  const changeIndex = config.findIndex((i) => i.key === changeKey);
  const navigate = useNavigate();
  const location = useLocation();

  const [numFilters, setNumFilters] = useState(0);
  const [items, setItems] = useState(config);

  useEffect(() => {
    if (changeKey && changeKeyUpdateHandlerCb) {
      setItems(changeKeyUpdateHandlerCb(items));
    }
  }, [items?.[changeIndex]?.data?.values?.length]);

  const urlParams = useQuery(queryParams);

  const clearAll = () => {
    const cleared = items.map((item) => {
      item.selected = false;
      item.data.values = [];
      return item;
    });

    const clearedFilterQuery = items.reduce(
      (obj: Record<string, string | null>, item) => {
        obj[item.key] = null;

        return obj;
      },
      {}
    );

    clearedFilterQuery.amount1 = null;
    clearedFilterQuery.amount2 = null;
    clearedFilterQuery.condition = null;
    clearedFilterQuery.store = null;
    accessLocalStorage.setSelectedEntityIds([]);

    navigate({
      search: QUERY.update(location.search, clearedFilterQuery),
    });
    setItems(cleared);
  };

  const [toggleDropdown, controlToggle] = useState(false);

  const applyFilter = () => {
    const query: {
      [key: string]: string;
    } = {
      tab: urlParams.get('tab') || 'details',
    };

    // Preserve subtab if it exists
    const currentSubtab = urlParams.get('subtab');
    if (currentSubtab) {
      query.subtab = currentSubtab;
    }
    const dates = urlParams.get('dates');
    if (dates) {
      query.dates = dates;
    }
    const isOrgLevel = urlParams.get('is_org_level');
    if (isOrgLevel) {
      query.is_org_level = isOrgLevel;
    }
    items.forEach((item) => {
      const { key, data, selected, type } = item;
      if (selected && data.values.length > 0) {
        if (type === EFilterConfigType.number) {
          const condition = data.values.shift();
          query.condition = condition || '';
          const firstValue = data.values.shift();
          const secondValue = data.values.shift();
          if (firstValue) {
            query[`${key}1`] = firstValue;
          }
          if (secondValue) {
            query[`${key}2`] = secondValue;
          }
        } else {
          const queryString = STRING.joinWithoutTrailingDelimiter(data.values);
          query[key] = queryString;
        }
      }
    });
    navigate({ search: `${QUERY.build(query)}` });
    controlToggle(false);
  };

  const getAppliedFromQuery = (configuration: IFilterConfig[]) =>
    configuration.map((item) => {
      const values = urlParams.get(item.key) || urlParams.get(`${item.key}1`);
      if (values) {
        if (item.type === EFilterConfigType.number) {
          const condition = urlParams.get('condition') || 'equal';
          item.data.values = [condition];

          const firstValue = urlParams.get(`${item.key}1`);
          const secondValue = urlParams.get(`${item.key}2`);
          if (firstValue) item.data.values.push(firstValue);
          if (secondValue) item.data.values.push(secondValue);
        } else {
          let val = values?.split(',');
          // Hack for URL encoding/decoding not working with React Router to add timezone
          if (item.type === EFilterConfigType.date) {
            val = val.map((v) => v.replace(' ', '+'));
          }
          item.data.values = val;
        }
        item.selected = true;
      }
      return item;
    });

  useEffect(() => {
    const prefilled = getAppliedFromQuery(config);
    setItems(prefilled);
  }, [config]);

  const setSelected = (key: string, value: boolean) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.selected = value;
      }
      return item;
    });
    setItems(updatedItems);
  };

  useEffect(() => {
    const numberSelected = items.reduce(
      (all, current) => (current.selected ? all + 1 : all),
      0
    );
    setNumFilters(numberSelected);
  }, [items]);

  /*
   *  Set single value in an array to generate comma-separated values
   */
  const setValue = (key: string, index: number, value: string) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.data.values[index] = value;
      }
      return item;
    });
    setItems(updatedItems);
  };

  /*
   *  Set entire array to generate comma-separated values
   */
  const setValues = (key: string, values: string[]) => {
    const updatedItems = items.map((item) => {
      if (item.key === key) {
        item.data.values = values;
      }
      return item;
    });
    setItems(updatedItems);
  };

  const getFilter = (item: IFilterConfig): JSX.Element => {
    switch (item.type) {
      case EFilterConfigType.date:
        return (
          <DateFilter
            setSelected={setSelected}
            item={item}
            setValue={setValue}
          />
        );
      case EFilterConfigType.number:
        return (
          <NumberFilter
            setSelected={setSelected}
            item={item}
            setValue={setValue}
          ></NumberFilter>
        );
      case EFilterConfigType.list:
        return (
          <ListFilter
            setSelected={setSelected}
            item={item}
            setValues={setValues}
          ></ListFilter>
        );
      case EFilterConfigType.string:
        return (
          <StringFilter
            setSelected={setSelected}
            item={item}
            setValue={setValue}
          ></StringFilter>
        );
      default:
        return <></>;
    }
  };

  return (
    <Popover open={toggleDropdown} onOpenChange={controlToggle}>
      <PopoverTrigger asChild>
        <Button
          data-testid="filter_button"
          variant="secondary"
          size="sm"
          iconStart="filter"
          iconEnd="chevron-down"
        >
          Filter{numFilters > 0 && ` (${numFilters})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="flex items-center justify-between px-2 py-1">
          <Button
            data-testid="clear"
            variant="ghost"
            size="sm"
            onClick={clearAll}
          >
            Clear
          </Button>
          <div className="font-semibold text-gray-blue-80">Filters</div>
          <Button
            data-testid="done"
            variant="treez-green"
            size="sm"
            onClick={applyFilter}
          >
            Done
          </Button>
        </div>
        <hr className="my-1 border-t border-gray-blue-10" />
        {items.map((item, index) => (
          <React.Fragment key={item.key}>
            {index > 0 && <hr className="my-1 border-t border-gray-blue-10" />}
            {getFilter(item)}
          </React.Fragment>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default Filter;
